import { useState } from "react";
import { ActivityIndicator, Pressable, View } from "react-native";
import SectionHeader from "../../ui-kit/section-header";
import { useTranslation } from "react-i18next";
import { useGetLanguages } from "@/src/hooks/features/settings/settings.hooks";
import LanguageItem from "./lang-item";
import { changeLanguage } from "@/src/i18n";
import { TickCircle } from "iconsax-react-nativejs";
import { useAuth } from "@/src/contexts/auth.context";
import { useUpdateUserPreferences } from "@/src/services/profile/profile.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { Bone, SkeletonPulse } from "../../ui-kit/skeleton";

const LANGUAGE_SKELETON_COUNT = 3;

/** Same pill shape as the language options: flag + name inside a bordered box. */
const LanguageOptionsSkeleton = () => (
  <SkeletonPulse testID="language-options-skeleton">
    <View className="flex-row flex-wrap gap-2">
      {Array.from({ length: LANGUAGE_SKELETON_COUNT }, (_, i) => (
        <View
          key={i}
          className="min-w-32 flex-row items-center px-4 py-3 gap-2 rounded-xl border border-ash-3 bg-chalk"
        >
          <Bone className="h-4 w-5 rounded-sm" />
          <Bone className="h-3 w-16" />
        </View>
      ))}
    </View>
  </SkeletonPulse>
);

const LanguageSection = () => {
  const { t, i18n } = useTranslation();
  const { themeVars } = useTheme();
  const { data: languages, isPending } = useGetLanguages();
  const [selectedCode, setSelectedCode] = useState(i18n.language);
  const [isChanging, setIsChanging] = useState(false);

  const { user } = useAuth();

  const { mutateAsync: updatePreferences } = useUpdateUserPreferences();
  const handleSelectLanguage = async (languageCode: string) => {
    if (languageCode === selectedCode) return;
    setIsChanging(true);
    setSelectedCode(languageCode);
    await changeLanguage(languageCode);
    // Only persist when we know whose profile to write. Previously this passed
    // `CreatedBy: user?.itemId` into a JSON filter string, so a missing user
    // silently produced `{"IsDeleted":false}` — matching *every* profile in the
    // org and rewriting everyone's language. The typed `where` makes the
    // undefined case a compile error instead.
    if (user?.itemId) {
      await updatePreferences({
        userId: user.itemId,
        language: languageCode,
      });
    }
    setIsChanging(false);
  };

  return (
    <View className="gap-4">
      <SectionHeader
        title={t("LABEL_LANGUAGE")}
        subtitle={t("LABEL_LANGUAGE_SETTINGS_DESC")}
      />
      {isPending ? (
        <LanguageOptionsSkeleton />
      ) : (
        <View className="flex-row flex-wrap gap-2">
          {languages?.map((lang) => {
            const isSelected = lang.languageCode === selectedCode;
            return (
              <Pressable
                key={lang.itemId}
                className={`min-w-32 flex-row items-center justify-start px-4 py-3 gap-2 rounded-xl border active:opacity-90 web:hover:border-ash-1 ${
                  isSelected
                    ? "bg-primary-2 border-primary hover:bg-primary-2"
                    : "bg-chalk border-ash-3 hover:bg-ash-3"
                }`}
                onPress={() => handleSelectLanguage(lang.languageCode)}
              >
                <LanguageItem
                  name={lang.languageName}
                  code={lang.languageCode}
                />
                {isChanging && isSelected ? (
                  <ActivityIndicator size="small" />
                ) : isSelected ? (
                  <TickCircle
                    size={20}
                    color={themeVars["--color-primary-default"] as string}
                    variant="Bold"
                  />
                ) : null}
              </Pressable>
            );
          })}
        </View>
      )}
    </View>
  );
};

export default LanguageSection;
